import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Divider,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Pagination,
  Button,
  InputAdornment
} from '@mui/material';
import { taskDataService, normalizeBranchesId } from '../../services/taskDataService';
import { surveyDataService } from '../../services/surveyDataService';
import { useAuth } from '../../contexts/AuthContext';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import Autocomplete from '@mui/material/Autocomplete';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { useTranslation } from 'react-i18next';

interface Task {
  id: string;
  name: string;
  description: string;
  personnelId?: string;
  branchId?: string;
}

interface Survey {
  id: string;
  title: string;
  branchId?: string;
}

interface ReportItem {
  taskId: string;
  taskName: string;
  surveyId: string;
  surveyTitle: string;
  answer: string;
  answerType: 'positive' | 'negative' | 'neutral';
  createdAt: number;
  responseId: string;
  questionTitle: string;
  personnelName: string;
  feedback?: string;
}

// SurveyReports interface'i
interface SurveyReportsProps {
  isManager?: boolean;
  branchId?: string;
  companyId: string;
  surveys?: Survey[]; // ✅ Surveys.tsx'ten gelen anket listesi
  allSurveys?: Survey[]; // ✅ Filtrelenmemiş tüm anketler
}

const SurveyReports: React.FC<SurveyReportsProps> = ({ 
  isManager = false, 
  branchId, 
  companyId,
  surveys: propSurveys = [], // ✅ Prop olarak gelen anketler
  allSurveys: propAllSurveys = [] // ✅ Prop olarak gelen tüm anketler
}) => {
  const { currentUser, branches } = useAuth();
  const { t } = useTranslation('common2');
  const [loading, setLoading] = useState(true);
  const [reportItems, setReportItems] = useState<ReportItem[]>([]);
  const [tasks, setTasks] = useState<Record<string, Task>>({});
  const [surveys, setSurveys] = useState<Record<string, Survey>>({});
  const [selectedTaskId, setSelectedTaskId] = useState<string>('');
  const [selectedSurveyId, setSelectedSurveyId] = useState<string>('');
  const [selectedBranchId, setSelectedBranchId] = useState<string>('');
  const UNASSIGNED_BRANCH_ID = '__unassigned__';

  // Manager ise, şube filtresini otomatik olarak kendi şubesine sabitle
  useEffect(() => {
    if (isManager && branchId) {
      setSelectedBranchId(branchId);
    }
  }, [isManager, branchId]);
  const [selectedAnswerType, setSelectedAnswerType] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [page, setPage] = useState(1);
  const itemsPerPage = 30;

  // Görevleri, anketleri ve cevapları yükle (Faz 8: tümü SQL)
  useEffect(() => {
    if (!currentUser || !companyId) return;

    const fetchData = async () => {
      try {
        // Görev verilerini SQL'den yükle (filtre dropdown'u + şube eşlemesi için).
        // answeredSurveys taskId (legacyFirebaseKey) ile anahtarlandığından
        // tasksData da legacyFirebaseKey || String(sqlId) ile anahtarlanır.
        const tasksData: Record<string, Task> = {};
        const taskItems: any[] = [];
        let tpage = 1;
        for (;;) {
          const res = await taskDataService.listTasks({ page: tpage, pageSize: 500 });
          taskItems.push(...res.items);
          if (res.items.length === 0 || taskItems.length >= (res.totalCount || 0)) break;
          tpage++;
        }
        taskItems.forEach((task: any) => {
          const key = task.legacyFirebaseKey || String(task.id);
          tasksData[key] = {
            id: key,
            name: task.name || t('surveys.surveyReports.defaultValues.unnamedTask'),
            description: task.description || '',
            personnelId: task.personnelFirebaseUid || '',
            branchId: normalizeBranchesId(task.branchesId) || ''
          };
        });
        setTasks(tasksData);

        // Anket filtre dropdown'u için anket listesi (prop'tan; yoksa SQL'den).
        const surveysData: Record<string, Survey> = {};
        const surveySource = (propAllSurveys && propAllSurveys.length > 0)
          ? propAllSurveys
          : (await surveyDataService.listAllSurveys()).map(s => ({
              id: s.id,
              title: s.title,
              branchId: s.branchId || undefined
            }));
        surveySource.forEach((survey) => {
          surveysData[survey.id] = {
            id: survey.id,
            title: survey.title || t('surveys.surveyReports.defaultValues.unnamedSurvey'),
            branchId: survey.branchId || undefined
          };
        });
        setSurveys(surveysData);

        // Rapor satırlarını SQL'den yükle (server-side join + denormalize).
        const rows = await surveyDataService.getAllReportRows();
        const reportItemsList: ReportItem[] = [];
        for (const row of rows) {
          // Manager filtresi: yalnızca kendi şubesindeki görevlerin yanıtları.
          if (isManager && branchId && row.branchId && row.branchId !== branchId) {
            continue;
          }
          const answerType: 'positive' | 'negative' | 'neutral' =
            row.answerType === 'positive' || row.answerType === 'negative' ? row.answerType : 'neutral';
          reportItemsList.push({
            taskId: row.taskFirebaseKey,
            taskName: row.taskName || tasksData[row.taskFirebaseKey]?.name || t('surveys.surveyReports.defaultValues.unnamedTask'),
            surveyId: row.surveyId != null ? String(row.surveyId) : '',
            surveyTitle: row.surveyTitle || t('surveys.surveyReports.defaultValues.unnamedSurvey'),
            answer: row.answer || '',
            answerType,
            createdAt: row.createdAt ? new Date(row.createdAt).getTime() : 0,
            responseId: String(row.responseId),
            questionTitle: row.questionTitle || '',
            personnelName: row.personnelName || t('surveys.surveyReports.defaultValues.unknown'),
            feedback: row.feedback || undefined
          });
        }

        reportItemsList.sort((a, b) => b.createdAt - a.createdAt);
        setReportItems(reportItemsList);
      } catch (error) {
        console.error('Anket raporları yüklenirken hata:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, companyId, isManager, branchId, propAllSurveys, t]);

  // Filtreleme fonksiyonu
  const filteredReportItems = reportItems.filter(item => {
    let matchesTask = true;
    let matchesSurvey = true;
    let matchesDateRange = true;
    let matchesAnswerType = true;
    let matchesBranch = true;

    if (selectedTaskId) {
      matchesTask = item.taskId === selectedTaskId;
    }

    if (selectedSurveyId) {
      matchesSurvey = item.surveyId === selectedSurveyId;
    }

    if (selectedAnswerType) {
      matchesAnswerType = item.answerType === selectedAnswerType;
    }

    if (startDate) {
      const itemDate = new Date(item.createdAt);
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      matchesDateRange = itemDate >= start;
    }

    if (endDate) {
      const itemDate = new Date(item.createdAt);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      matchesDateRange = matchesDateRange && itemDate <= end;
    }

    // Şube filtresi: görev üzerinden filtrele
    if (selectedBranchId) {
      const taskBranchId = tasks[item.taskId]?.branchId || '';
      if (selectedBranchId === UNASSIGNED_BRANCH_ID) {
        matchesBranch = !taskBranchId; // branchId yok/boş olan görevler
      } else {
        matchesBranch = taskBranchId === selectedBranchId;
      }
      if (!matchesBranch) {
        return false;
      }
    }

    return matchesTask && matchesSurvey && matchesAnswerType && matchesDateRange && matchesBranch;
  });

  // Sayfa değiştirme işleyicisi
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  // Anket cevaplarını göreve ve yanıt ID'sine göre grupla
  const groupedReports: { [key: string]: ReportItem[] } = {};
  filteredReportItems.forEach(item => {
    const key = `${item.taskId}-${item.responseId}`;
    if (!groupedReports[key]) {
      groupedReports[key] = [];
    }
    groupedReports[key].push(item);
  });

  // Grupları listeye dönüştür
  const groupedReportsList = Object.values(groupedReports);

  // Toplam sayfa sayısını hesapla
  const totalPages = Math.max(1, Math.ceil(groupedReportsList.length / itemsPerPage));

  // Geçerli sayfayı sınırla
  useEffect(() => {
    if (page > totalPages) {
      setPage(1);
    }
  }, [totalPages, page]);

  // Sayfalandırılmış grupları al
  const paginatedGroups = groupedReportsList.slice(
    (page - 1) * itemsPerPage,
    Math.min(page * itemsPerPage, groupedReportsList.length)
  );

  // Excel dışa aktarma fonksiyonu
  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t('surveys.surveyReports.excel.sheetName'));

      worksheet.columns = [
        { header: t('surveys.surveyReports.excel.headers.taskName'), key: 'taskName', width: 30 },
        { header: t('surveys.surveyReports.excel.headers.personnel'), key: 'personnelName', width: 20 },
        { header: t('surveys.surveyReports.excel.headers.surveyQuestion'), key: 'questionTitle', width: 40 },
        { header: t('surveys.surveyReports.excel.headers.surveyAnswer'), key: 'answer', width: 30 },
        { header: t('surveys.surveyReports.excel.headers.answerType'), key: 'answerType', width: 15 },
        { header: t('surveys.surveyReports.excel.headers.responseDate'), key: 'createdAt', width: 20 },
        { header: t('surveys.surveyReports.excel.headers.feedback'), key: 'feedback', width: 40 }
      ];

      const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFFFF' } },
        fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FF4F81BD' } },
        border: {
          top: { style: 'thin' as const },
          left: { style: 'thin' as const },
          bottom: { style: 'thin' as const },
          right: { style: 'thin' as const }
        }
      };

      worksheet.getRow(1).eachCell(cell => {
        cell.style = headerStyle;
      });

      filteredReportItems.forEach(item => {
        const answerTypeText = item.answerType === 'positive'
          ? t('surveys.surveyReports.excel.answerTypes.positive')
          : item.answerType === 'negative'
            ? t('surveys.surveyReports.excel.answerTypes.negative')
            : t('surveys.surveyReports.excel.answerTypes.neutral');

        const createdAtDate = new Date(item.createdAt).toLocaleString(navigator.language, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });

        worksheet.addRow({
          taskName: item.taskName,
          personnelName: item.personnelName,
          questionTitle: item.questionTitle,
          answer: item.answer,
          answerType: answerTypeText,
          createdAt: createdAtDate,
          feedback: item.feedback || t('surveys.surveyReports.defaultValues.noFeedback')
        });
      });

      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) {
          const fillColor = rowNumber % 2 === 0 ? 'FFF2F2F2' : 'FFFFFFFF';
          row.eachCell(cell => {
            cell.style = {
              fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: fillColor } },
              border: {
                top: { style: 'thin' as const },
                left: { style: 'thin' as const },
                bottom: { style: 'thin' as const },
                right: { style: 'thin' as const }
              }
            };
          });
        }
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      saveAs(new Blob([buffer]), `${t('surveys.surveyReports.excel.fileName')}_${currentDate}.xlsx`);

    } catch (error) {
      alert(t('surveys.surveyReports.excel.error'));
    }
  };

  return (
    <Paper sx={{ p: { xs: 1, sm: 2, md: 3 }, overflowX: 'hidden' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" component="h2">
          {t('surveys.surveyReports.title')}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            color="info"
            startIcon={<DownloadIcon />}
            onClick={exportToExcel}
            disabled={loading || filteredReportItems.length === 0}
            size="small"
          >
            {t('surveys.surveyReports.downloadExcel')}
          </Button>
        </Stack>
      </Box>
      <Divider sx={{ mb: 2 }} />

      {/* Filtre Bölümü */}
      <Box sx={{ mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
          <FormControl fullWidth size="small">
            <Autocomplete
              size="small"
              options={Object.values(tasks)}
              getOptionLabel={(option) => option.name || ''}
              value={selectedTaskId ? tasks[selectedTaskId] || null : null}
              onChange={(event, newValue) => {
                setSelectedTaskId(newValue?.id || '');
                setPage(1);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('surveys.surveyReports.filters.surveyFilter')}
                  margin="dense"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <MenuItem {...props}>
                  {option.name}
                </MenuItem>
              )}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              noOptionsText={t('tasks.noTasks')}
              clearText={t('reports.clearAll')}
              openText={t('reports.open')}
              closeText={t('reports.close')}
            />
          </FormControl>

          <FormControl fullWidth size="small">
            <Autocomplete
              size="small"
              options={Object.values(surveys)}
              getOptionLabel={(option) => option.title || ''}
              value={selectedSurveyId ? surveys[selectedSurveyId] || null : null}
              onChange={(event, newValue) => {
                setSelectedSurveyId(newValue?.id || '');
                setPage(1);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('surveys.surveyReports.filters.questionFilter')}
                  margin="dense"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <MenuItem {...props}>
                  {option.title}
                </MenuItem>
              )}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              noOptionsText={t('surveys.list.noSurveys')}
              clearText={t('reports.clearAll')}
              openText={t('reports.open')}
              closeText={t('reports.close')}
            />
          </FormControl>

          <FormControl fullWidth size="small">
            <Autocomplete
              size="small"
              options={[{ id: UNASSIGNED_BRANCH_ID, name: t('surveys.surveyReports.filters.unassignedBranch') }, ...branches]}
              getOptionLabel={(option) => option.name || ''}
              value={
                selectedBranchId 
                  ? ([UNASSIGNED_BRANCH_ID].includes(selectedBranchId) 
                      ? { id: UNASSIGNED_BRANCH_ID, name: t('surveys.surveyReports.filters.unassignedBranch') } 
                      : (branches.find(b => b.id === selectedBranchId) || null))
                  : null
              }
              onChange={(event, newValue) => {
                setSelectedBranchId(newValue?.id || '');
                setPage(1);
              }}
              disabled={Boolean(isManager && branchId)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('surveys.surveyReports.filters.branchFilter')}
                  margin="dense"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <MenuItem {...props}>
                  {option.name}
                </MenuItem>
              )}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              noOptionsText={t('surveys.surveyReports.filters.noBranches')}
              clearText={t('reports.clearAll')}
              openText={t('reports.open')}
              closeText={t('reports.close')}
            />
          </FormControl>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField
            label={t('surveys.surveyReports.filters.startDate')}
            type="date"
            size="small"
            margin="dense"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={startDate ? startDate.toISOString().split('T')[0] : ''}
            onChange={(e) => {
              if (e.target.value) {
                setStartDate(new Date(e.target.value));
              } else {
                setStartDate(null);
              }
              setPage(1);
            }}
          />

          <TextField
            label={t('surveys.surveyReports.filters.endDate')}
            type="date"
            size="small"
            margin="dense"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={endDate ? endDate.toISOString().split('T')[0] : ''}
            onChange={(e) => {
              if (e.target.value) {
                setEndDate(new Date(e.target.value));
              } else {
                setEndDate(null);
              }
              setPage(1);
            }}
          />

          <FormControl fullWidth size="small">
            <InputLabel>{t('surveys.surveyReports.filters.answerType')}</InputLabel>
            <Select
              margin="dense"
              value={selectedAnswerType}
              label={t('surveys.surveyReports.filters.answerType')}
              onChange={(e) => {
                setSelectedAnswerType(e.target.value as string);
                setPage(1);
              }}
            >
              <MenuItem value="">{t('surveys.surveyReports.filters.all')}</MenuItem>
              <MenuItem value="positive">{t('surveys.surveyReports.answerTypes.positive')}</MenuItem>
              <MenuItem value="negative">{t('surveys.surveyReports.answerTypes.negative')}</MenuItem>
              <MenuItem value="neutral">{t('surveys.surveyReports.answerTypes.neutral')}</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Box>

      {/* Rapor Tablosu */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress />
        </Box>
      ) : groupedReportsList.length === 0 ? (
        <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
          {t('surveys.surveyReports.noData')}
        </Typography>
      ) : (
        <>
          <TableContainer sx={{ maxWidth: '100%' }}>
            <Table size="small" sx={{ minWidth: { xs: 500, sm: 650 } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 100, fontSize: '0.75rem', padding: 0.5, fontWeight: 'bold' }}>{t('surveys.surveyReports.table.taskName')}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 130, fontSize: '0.75rem', padding: 0.5, fontWeight: 'bold' }}>{t('surveys.surveyReports.table.surveyQuestion')}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 80, fontSize: '0.75rem', padding: 0.5, fontWeight: 'bold' }}>{t('surveys.surveyReports.table.surveyAnswer')}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 100, fontSize: '0.75rem', padding: 0.5, fontWeight: 'bold' }}>{t('surveys.surveyReports.table.responseDate')}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 150, fontSize: '0.75rem', padding: 0.5, fontWeight: 'bold' }}>{t('surveys.surveyReports.table.feedback')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedGroups.map((group, groupIndex) => (
                  <React.Fragment key={`group-${groupIndex}`}>
                    {group.map((item, itemIndex) => (
                      <TableRow key={`${item.responseId}-${item.surveyId}`}>
                        {itemIndex === 0 && (
                          <TableCell rowSpan={group.length} sx={{ padding: 0.5 }}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: { xs: 90, sm: 140 }, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.75rem' }}>
                              {item.taskName}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell sx={{ padding: 0.5 }}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: { xs: 90, sm: 170 }, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.75rem' }}>
                            {item.questionTitle}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ padding: 0.5 }}>
                          <Chip
                            label={item.answer}
                            size="small"
                            color={
                              item.answerType === 'positive' 
                                ? 'success' 
                                : item.answerType === 'negative' 
                                ? 'error' 
                                : 'default'
                            }
                            sx={{ 
                              maxWidth: { xs: 65, sm: 100 }, 
                              height: 20,
                              "& .MuiChip-label": { 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis', 
                                fontSize: '0.65rem',
                                px: 0.75 
                              } 
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ padding: 0.5 }}>
                          <Typography variant="body2" noWrap sx={{ fontSize: '0.75rem' }}>
                            {new Date(item.createdAt).toLocaleString()}
                          </Typography>
                        </TableCell>
                        {itemIndex === 0 && (
                          <TableCell rowSpan={group.length} sx={{ padding: 0.5 }}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                maxWidth: { xs: 120, sm: 200 }, 
                                fontSize: '0.75rem',
                                wordBreak: 'break-word'
                              }}
                            >
                              {item.feedback || t('surveys.surveyReports.defaultValues.noFeedback')}
                            </Typography>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size="small"
              showFirstButton
              showLastButton
            />
          </Box>
        </>
      )}
    </Paper>
  );
};

export default SurveyReports;
